import {
  Box, Button, Drawer, Grid, TextField, Typography, FormControlLabel, Checkbox
} from '@mui/material';
import { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiStatus } from "../../utils/api.types";
import { ICostCategoryFormInputs } from '../../utils/costCategory.types';
import { createCostCategory, updateCostCategory, fetchCostCategories } from '../../store/costCategoryStore';

interface DialogCostCategoryFormProps {
  open: boolean;
  onClose: () => void;
  editData?: any;
}

const schema = yup.object().shape({
  name: yup.string().required("Category Name is required").min(2, "Name must be at least 2 characters").max(100, "Name must not exceed 100 characters"),
  alias: yup.string().max(20, "Alias must not exceed 20 characters"),
  description: yup.string().max(500, "Description must not exceed 500 characters"),
  isActive: yup.boolean()
});

const DialogCostCategoryForm: FC<DialogCostCategoryFormProps> = ({ onClose, open, editData }) => {
  const { status } = useAppSelector(state => state.costCategories);
  const dispatch = useAppDispatch();

  const methods = useForm<ICostCategoryFormInputs>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      alias: '',
      description: '',
      isActive: true
    }
  });

  const { handleSubmit, reset, formState: { errors } } = methods;

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      reset({
        name: editData.name || '',
        alias: editData.alias || '',
        description: editData.description || '',
        isActive: editData.isActive !== undefined ? editData.isActive : true
      });
    } else {
      reset({
        name: '',
        alias: '',
        description: '',
        isActive: true
      });
    }
  }, [editData, reset]);

  const onSubmit = async (data: ICostCategoryFormInputs) => {
    try {
      if (editData?._id) {
        await dispatch(updateCostCategory({ id: editData._id, data })).unwrap();
      } else {
        await dispatch(createCostCategory(data)).unwrap();
      }
      await dispatch(fetchCostCategories(true));
      reset();
      onClose();
    } catch (error) {
      console.error('Error saving cost category:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500 },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {editData ? 'Edit Cost Category' : 'Add Cost Category'}
            </Typography>
          </Box>

          {/* Content - Scrollable */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Category Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="e.g., Branches, Departments, Projects"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="alias"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Alias (Short Code)"
                      fullWidth
                      error={!!errors.alias}
                      helperText={errors.alias?.message}
                      placeholder="e.g., BR, DEPT, PROJ"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      placeholder="Optional description for this category"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="isActive"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Footer - Fixed at bottom */}
          <Box sx={{
            borderTop: '1px solid #e5e7eb',
            p: 2,
            backgroundColor: '#f9fafb',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={status === ApiStatus.LOADING}
              sx={{
                minWidth: 100,
                height: 40,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={status === ApiStatus.LOADING}
              sx={{
                minWidth: 140,
                height: 40,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {editData ? 'Update' : 'Save'}
            </LoadingButton>
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

export default DialogCostCategoryForm;
